import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { LocaleSettings } from '../locale-settings';
import { LocalizationStrategy, mapTransKeyToLocale } from '../localization';
import { PreferencesCookieService } from '@app/core/cookies';
import { Logger } from '@app/core/logging';
import { isPlatformServer } from '@angular/common';

@Injectable()
export class HostnameLocalizationService extends LocalizationStrategy {
  private hostname;
  private defaultSettings: Map<string, string> = new Map([
    ['www', '4.en.GBP'],
    ['us', '6.us.USD'],
    ['fr', '1.fr.EUR'],
    ['de', '10.de.EUR'],
    ['es', '12.es.EUR'],
    ['it', '11.it.EUR']
  ]);

  constructor(
    @Inject('HOST') hostname: string,
    private logger: Logger,
    @Inject('COOKIES') private cookies: Object,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    super();
    this.setHostname(hostname);
  }

  setHostname(hostname: string): void {
    const index = hostname.indexOf(':');

    if (index > 0) {
      this.hostname = hostname.substring(0, index);
    } else {
      this.hostname = hostname;
    }
  }

  getHostname(): string {
    return this.hostname;
  }

  private resolve(): string {
    let key = '';
    const tokens = this.hostname.split('.');

    if (tokens.length > 1) {
      key = tokens[0];
    }

    return this.defaultSettings.get(key);
  }

  decode(data: string): any {
    const search = { '-': '+', _: '/' };
    const encoded = data.replace(/\-|\_/gi, matched => search[matched]);
    const padding = new Array((encoded.length % 4) + 1);
    return JSON.parse(Buffer.from(encoded + padding.join('='), 'base64').toString());
  }

  localize(): LocaleSettings {
    if (
      this.cookies[PreferencesCookieService.CK_COOKIE] &&
      isPlatformServer(this.platformId) &&
      this.cookies[PreferencesCookieService.CC_COOKIE]
    ) {
      const ckCookie = PreferencesCookieService.parseCkCookie(this.cookies[PreferencesCookieService.CK_COOKIE]);
      const ccCookie = this.decode(this.cookies[PreferencesCookieService.CC_COOKIE]);
      this.logger.info(`Localizes from cookies vc_ck: ${JSON.stringify(ckCookie)}`);
      this.logger.info(`Localizes from cookies vc_cc: ${JSON.stringify(ccCookie)}`);
      return new LocaleSettings(
        mapTransKeyToLocale(ckCookie.languageCode),
        parseInt(ckCookie.regionId, 10),
        ckCookie.currencyCode,
        ccCookie.CC
      );
    } else {
      this.logger.info(`Localizes from ${this.hostname}`);
      const cacheKey = this.resolve();
      const ckCookie = PreferencesCookieService.parseCkCookie(cacheKey);
      if (cacheKey === undefined || ckCookie === undefined) {
        return LocaleSettings.DEFAULT;
      }
      this.logger.info(`Forces ${this.hostname} localization to ${cacheKey}`);
      return new LocaleSettings(
        mapTransKeyToLocale(ckCookie.languageCode),
        parseInt(ckCookie.regionId, 10),
        ckCookie.currencyCode
      );
    }
  }
}
